/**
 * UserContentFeedback — ratings & comments (registered users); gated by Visibility + server policy.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVisibility } from '../context/VisibilityContext';
import './UserContentFeedback.css';

function normRating(r) {
  if (r == null || r === '') return null;
  const n = Number(r);
  return Number.isFinite(n) ? n : null;
}

function normComment(s) {
  return (s || '').trim();
}

/**
 * Match backend getFeedbackPolicy: split key in public settings wins; only one split ⇒ other off; else BOTH (legacy).
 */
function effectiveFeedbackChannel(settings, legacyKey, ratingsKey, commentsKey, channel, loading) {
  if (loading || !legacyKey || !ratingsKey || !commentsKey) return false;
  const hasR = Object.prototype.hasOwnProperty.call(settings, ratingsKey);
  const hasC = Object.prototype.hasOwnProperty.call(settings, commentsKey);
  const legacyOn = !!settings[legacyKey];
  if (channel === 'ratings') {
    if (hasR) return !!settings[ratingsKey];
    if (hasC) return false;
    return legacyOn;
  }
  if (channel === 'comments') {
    if (hasC) return !!settings[commentsKey];
    if (hasR) return false;
    return legacyOn;
  }
  return false;
}

/** True if public settings include any key for this feedback surface (so "all false" is intentional). */
function hasAnyFeedbackKey(settings, legacyKey, ratingsKey, commentsKey) {
  return (
    (legacyKey && Object.prototype.hasOwnProperty.call(settings, legacyKey)) ||
    (ratingsKey && Object.prototype.hasOwnProperty.call(settings, ratingsKey)) ||
    (commentsKey && Object.prototype.hasOwnProperty.call(settings, commentsKey))
  );
}

const UserContentFeedback = ({
  targetType,
  targetId,
  settingKey,
  ratingsSettingKey,
  commentsSettingKey,
  title = 'Ratings & comments',
}) => {
  const { isAuthenticated } = useAuth();
  const { loading: visibilityLoading, settings } = useVisibility();

  const legacyKey = settingKey || null;
  const ratingsKey =
    ratingsSettingKey ??
    (legacyKey ? legacyKey.replace(/_enabled$/, '_ratings_enabled') : null);
  const commentsKey =
    commentsSettingKey ??
    (legacyKey ? legacyKey.replace(/_enabled$/, '_comments_enabled') : null);

  const effectiveRatings = effectiveFeedbackChannel(
    settings,
    legacyKey,
    ratingsKey,
    commentsKey,
    'ratings',
    visibilityLoading,
  );
  const effectiveComments = effectiveFeedbackChannel(
    settings,
    legacyKey,
    ratingsKey,
    commentsKey,
    'comments',
    visibilityLoading,
  );

  /**
   * Fetch from API when client thinks feature may be on, or when public settings omit keys
   * (so we still probe — otherwise the widget shell renders with data=null and no form forever).
   */
  const shouldFetchFeedback =
    !visibilityLoading &&
    (effectiveRatings ||
      effectiveComments ||
      !hasAnyFeedbackKey(settings, legacyKey, ratingsKey, commentsKey));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftRating, setDraftRating] = useState(null);
  const [draftComment, setDraftComment] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!targetType || !targetId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/content-feedback/${targetType}/${encodeURIComponent(String(targetId))}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!res.ok) throw new Error('Failed to load feedback');
      const json = await res.json();
      const mineFromRow = (json.comments || []).find((c) => c.isMine);
      const mine =
        json.mine ||
        (mineFromRow
          ? {
              rating: mineFromRow.rating,
              comment: mineFromRow.comment || '',
            }
          : null);
      const merged = mine != null ? { ...json, mine } : json;
      setData(merged);
      if (mine) {
        setDraftRating(normRating(mine.rating));
        setDraftComment(mine.comment || '');
      } else {
        setDraftRating(null);
        setDraftComment('');
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId]);

  useEffect(() => {
    if (visibilityLoading || !shouldFetchFeedback) return;
    load();
  }, [visibilityLoading, shouldFetchFeedback, load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated || !data) return;
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      if (data.commentsEnabled) {
        payload.comment = draftComment;
      }
      if (data.ratingsEnabled) {
        const dr = normRating(draftRating);
        if (dr != null) {
          payload.rating = dr;
        } else if (data.mine != null && normRating(data.mine.rating) != null) {
          payload.rating = null;
        }
      }
      if (Object.keys(payload).length === 0) {
        setSaving(false);
        return;
      }
      const res = await fetch(
        `/api/content-feedback/${targetType}/${encodeURIComponent(String(targetId))}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || 'Save failed');
      }
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (visibilityLoading) return null;

  if (!shouldFetchFeedback) return null;

  if (!loading && data && data.enabled === false) return null;

  const mineR = normRating(data?.mine?.rating);
  const mineC = normComment(data?.mine?.comment);
  const draftR = normRating(draftRating);
  const ratingDirty = !!data?.ratingsEnabled && draftR !== mineR;
  const commentDirty = !!data?.commentsEnabled && normComment(draftComment) !== mineC;
  const canSubmit =
    !!data &&
    data.enabled &&
    (ratingDirty ||
      commentDirty ||
      (!data.mine &&
        ((data.ratingsEnabled && draftR != null) ||
          (data.commentsEnabled && normComment(draftComment) !== ''))));

  const returnUrl =
    typeof window !== 'undefined'
      ? encodeURIComponent(window.location.pathname + window.location.search)
      : '';

  const loginHintSuffix =
    data?.ratingsEnabled && data?.commentsEnabled
      ? 'to leave a rating or comment.'
      : data?.ratingsEnabled
        ? 'to leave a rating.'
        : 'to leave a comment.';

  const starActive = (n) => draftR != null && draftR >= n;

  return (
    <section className="user-content-feedback user-content-feedback--compact" aria-labelledby="ucf-heading">
      <div className="ucf-head">
        <h2 id="ucf-heading" className="ucf-title">
          {title}
        </h2>
        {data && data.enabled && data.ratingsEnabled ? (
          <div className="ucf-summary ucf-summary--inline">
            {data.averageRating != null && data.ratingCount > 0 ? (
              <p className="ucf-summary-line">
                <span className="ucf-stars" aria-hidden="true">
                  {'★'.repeat(Math.round(data.averageRating))}
                  {'☆'.repeat(Math.max(0, 5 - Math.round(data.averageRating)))}
                </span>
                <strong className="ucf-score">{data.averageRating}</strong>
                <span className="ucf-score-denom">/5</span>
                <span className="ucf-muted">({data.ratingCount})</span>
              </p>
            ) : (
              <p className="ucf-muted ucf-summary-line">No ratings yet. Be the first.</p>
            )}
          </div>
        ) : null}
      </div>

      {loading && <p className="ucf-muted">Loading…</p>}
      {error && <p className="ucf-error">{error}</p>}

      {data && data.enabled && (
        <>
          {isAuthenticated ? (
            <form className="ucf-form" onSubmit={handleSubmit}>
              {data.ratingsEnabled ? (
                <>
                  <p className="ucf-label">Your rating</p>
                  <div className="ucf-star-input" role="group" aria-label="Star rating 1 to 5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`ucf-star-btn ${starActive(n) ? 'active' : ''}`}
                        onClick={() => setDraftRating(n)}
                        aria-pressed={starActive(n)}
                        aria-label={`${n} star${n === 1 ? '' : 's'}`}
                      >
                        ★
                      </button>
                    ))}
                    {draftR != null && (
                      <button
                        type="button"
                        className="ucf-clear-stars"
                        onClick={() => setDraftRating(null)}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </>
              ) : null}

              {data.commentsEnabled ? (
                <>
                  <label
                    className="ucf-label"
                    htmlFor={`ucf-comment-${targetType}-${targetId}`}
                  >
                    {data.ratingsEnabled ? 'Comment (optional)' : 'Your comment'}
                  </label>
                  <textarea
                    id={`ucf-comment-${targetType}-${targetId}`}
                    className="ucf-textarea"
                    rows={3}
                    maxLength={2000}
                    value={draftComment}
                    onChange={(ev) => setDraftComment(ev.target.value)}
                    placeholder="Share your thoughts…"
                  />
                </>
              ) : null}

              <button type="submit" className="ucf-submit" disabled={saving || !canSubmit}>
                {saving ? 'Saving…' : data.mine ? 'Update' : 'Submit'}
              </button>
            </form>
          ) : (
            <p className="ucf-login-hint">
              <Link to={`/login?returnUrl=${returnUrl}`}>Sign in</Link> {loginHintSuffix}
            </p>
          )}

          {data.comments && data.comments.length > 0 && (
            <ul className="ucf-list">
              {data.comments.map((c) => (
                <li key={c.id} className="ucf-item">
                  <div className="ucf-item-head">
                    <span className="ucf-author">{c.displayName || c.username}</span>
                    {data.ratingsEnabled && c.rating != null && (
                      <span className="ucf-item-stars" aria-label={`${c.rating} of 5`}>
                        {'★'.repeat(c.rating)}
                        {'☆'.repeat(5 - c.rating)}
                      </span>
                    )}
                    <time className="ucf-date" dateTime={c.createdAt}>
                      {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}
                    </time>
                  </div>
                  {c.comment ? <p className="ucf-item-text">{c.comment}</p> : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

export default UserContentFeedback;
